// PO Toolkit v2.0.0 - Standalone SaaS
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/context/AuthContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { useAuth } from "@/hooks/useAuth";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ReactNode, Suspense, lazy } from 'react';
import { PageLoading } from "@/components/ui/LoadingSpinner";

// Lazy Loaded Views

const ResumeGrid = lazy(() => import("./views/ResumeGrid"));
const AuthPage = lazy(() => import("./views/auth/AuthPage"));
const Dashboard = lazy(() => import("./views/dashboard/Dashboard"));
const PersonaList = lazy(() => import("./views/personas/PersonaList"));
const PersonaDetail = lazy(() => import("./views/personas/PersonaDetail"));
const MapList = lazy(() => import("./views/maps/MapList"));
const MapEditor = lazy(() => import("./views/maps/MapEditor"));
const TeamsList = lazy(() => import("./views/teams/TeamsList"));
const ProfilePage = lazy(() => import("./views/settings/ProfilePage"));
const WorkspaceSettingsPage = lazy(() => import("./views/settings/WorkspaceSettingsPage"));
const IntegrationsPage = lazy(() => import("./views/integrations/IntegrationsPage"));
const FeedbackPage = lazy(() => import("./views/feedback/FeedbackPage"));

const AcceptInvite = lazy(() => import("./views/invite/AcceptInvite"));
const AdminDashboard = lazy(() => import("./views/admin/AdminDashboard"));
const NotFound = lazy(() => import("./views/NotFound"));
const PrototypeLoader = lazy(() => import("@/prototypes/PrototypeLoader"));


// Layouts
import SuperAdminLayout from "./layouts/SuperAdminLayout";
import MainLayout from "./layouts/MainLayout";
import RoadmapToolkitRoutes from "../apps/roadmap-toolkit/routes";
const StoryMapPage = lazy(() => import("../apps/roadmap-toolkit/story-map/StoryMap").then(m => ({ default: m.StoryMap })));

const queryClient = new QueryClient();

const ProtectedLayout = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoading />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <WorkspaceProvider>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Toaster />
              <Sonner />
              <Suspense fallback={<PageLoading />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Navigate to="/auth" replace />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/resume" element={<ResumeGrid />} />

                  {/* Invite Acceptance (semi-public) */}
                  <Route path="/invite/:token" element={<AcceptInvite />} />

                  {/* Legacy Protected Routes (backwards compatible) */}
                  <Route
                    element={
                      <ProtectedLayout>
                        <MainLayout />
                      </ProtectedLayout>
                    }
                  >
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/personas" element={<PersonaList />} />
                    <Route path="/personas/:id" element={<PersonaDetail />} />
                    <Route path="/maps" element={<MapList />} />
                    <Route path="/maps/:id" element={<MapEditor />} />
                    <Route path="/teams" element={<TeamsList />} />
                    <Route path="/account" element={<ProfilePage />} />
                    <Route path="/settings" element={<WorkspaceSettingsPage />} />
                    <Route path="/integrations" element={<IntegrationsPage />} />
                    <Route path="/feedback" element={<FeedbackPage />} />
                    <Route path="/w/:workspaceId/feedback" element={<FeedbackPage />} />

                    {/* Workspace-Scoped Routes (new) */}
                    <Route path="/w/:workspaceId/dashboard" element={<Dashboard />} />
                    <Route path="/w/:workspaceId/personas" element={<PersonaList />} />
                    <Route path="/w/:workspaceId/personas/:id" element={<PersonaDetail />} />
                    <Route path="/w/:workspaceId/maps" element={<MapList />} />
                    <Route path="/w/:workspaceId/maps/:id" element={<MapEditor />} />
                    <Route path="/w/:workspaceId/settings" element={<WorkspaceSettingsPage />} />

                    <Route path="/story-maps/:storyMapId" element={<StoryMapPage />} />
                    <Route path="/roadmaps/*" element={<RoadmapToolkitRoutes />} />
                  </Route>

                  {/* Super Admin Routes */}
                  <Route path="/admin" element={<SuperAdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<AdminDashboard />} />
                    <Route path="health" element={<AdminDashboard />} />
                  </Route>

                  {/* Prototype Routes */}
                  <Route path="/prototypes/:id/*" element={<PrototypeLoader />} />


                  {/* Fallback */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </WorkspaceProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
